import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useBreakoutUser } from 'hooks/profile'
import { useCubitBuilder } from 'hooks/cubits'
import { useTranslationTyped } from 'i18n/i18n'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { Spinner } from 'components/Spinner'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { ScoreMappingScales } from 'pages/instructor/assignment/dialogs/ScoreMappingScales'
import { ScoreMappingDefaultsCubit } from 'pages/instructor/assignment/dialogs/ScoreMappingDefaultsCubit'
import { SCALES } from 'pages/instructor/assignment/dialogs/customRubricsConfig'

/**
 * Settings subpage where an instructor edits the default score mappings applied
 * when they turn on custom grading. Renders the same tabbed editor as the
 * per-assignment column (all scales), seeded from the saved defaults or — when
 * none are saved — the implicit global default mapping, and persists the whole
 * set via the score-mapping-defaults API. All load/seed/validate/save state
 * lives in a shared ScoreMappingDefaultsCubit; this view is a thin observer.
 */
export const ProfileScoreMappingDefaults = observer(
  function ProfileScoreMappingDefaults() {
    const { tt } = useTranslationTyped()
    const user = useBreakoutUser()
    const cubit = useCubitBuilder(
      () =>
        new ScoreMappingDefaultsCubit(user, { managesAssignFinalGrade: true }),
      [user]
    )

    const handleSave = useCallback(async () => {
      const saved = await cubit.save()
      if (saved) {
        toast.success(tt.profile.score_mapping_defaults_saved())
      } else {
        toast.error(tt.profile.score_mapping_defaults_save_failed())
      }
    }, [cubit, tt])

    if (cubit.status === 'error') {
      return (
        <div className="flex max-w-md flex-col gap-6">
          <div>
            <div className="text-headline-large text-core-on-tertiary">
              {tt.profile.score_mapping_defaults()}
            </div>
            <div className="mt-1 text-body-large text-core-on-tertiary">
              {tt.profile.score_mapping_defaults_description()}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
            <TriangleAlertIcon size={24} className="stroke-core-error" />
            <div className="text-body-medium text-on-surface-var">
              {tt.profile.score_mapping_defaults_load_failed()}
            </div>
          </div>
        </div>
      )
    }

    if (cubit.status === 'loading') {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size={3} />
        </div>
      )
    }

    return (
      <div className="flex max-w-md flex-col gap-6">
        <div>
          <div className="text-headline-large text-core-on-tertiary">
            {tt.profile.score_mapping_defaults()}
          </div>
          <div className="mt-1 text-body-large text-core-on-tertiary">
            {tt.profile.score_mapping_defaults_description()}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-title-medium">
            {tt.profile.score_mapping_defaults_grading_settings()}
          </h3>
          <div className="flex items-center justify-between gap-4">
            <div className="text-body-medium">
              {tt.profile.score_mapping_defaults_assign_final_grade()}
            </div>
            <BreakoutSwitch
              checked={cubit.assignFinalGrade}
              onChange={(e) => cubit.setAssignFinalGrade(e.target.checked)}
              testId="assign-final-grade-toggle"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center gap-1">
            <h3 className="text-title-medium">
              {tt.instructor_library.custom_rubrics_score_mapping_title()}
            </h3>
            <BreakoutInfoTooltip
              content={tt.instructor_library.custom_rubrics_score_mapping_tooltip()}
            />
          </div>

          <ScoreMappingScales
            scales={SCALES}
            values={cubit.mappings}
            onChange={(scaleId, next) => cubit.changeMapping(scaleId, next)}
          />
        </div>

        <div>
          <BreakoutButton
            kind="accent"
            size="large"
            disabled={!cubit.canSave}
            onClick={handleSave}
            data-testid="score-mapping-defaults-save"
          >
            {tt.profile.score_mapping_defaults_save()}
          </BreakoutButton>
        </div>
      </div>
    )
  }
)
